import { ApiClientConfig, ApiClientPlatformConfig } from "../api-client";

/**
 * Storage keys used by the local storage adapter.
 * These keys are used to store various tokens and timestamps in localStorage.
 *
 * - ACCESS_TOKEN: The current access token for API requests
 * - ACCESS_EXPIRES_AT: Timestamp when the access token expires
 * - REFRESH_TOKEN: Token used to obtain new access tokens
 * - REFRESH_EXPIRES_AT: Timestamp when the refresh token expires
 * - ACCOUNT_ID: The current user's account identifier
 * - LOCAL_REFRESHED_TIME: Timestamp of the last token refresh
 */
const STORAGE_KEYS = {
	ACCESS_TOKEN: "verityToken",
	ACCESS_EXPIRES_AT: "verityAccessExpiresAt",
	REFRESH_TOKEN: "verityInfo",
	REFRESH_EXPIRES_AT: "verityRefreshExpiresAt",
	ACCOUNT_ID: "verityAccountId",
	LOCAL_REFRESHED_TIME: "verityLocalRefreshTimestamp",
} as const;

/**
 * Creates an ApiClientConfig implementation that uses localStorage for token storage.
 * This adapter provides a persistent storage solution for browser environments.
 *
 * @param config - Platform configuration including Verity and Lockdown settings
 * @returns An ApiClientConfig implementation using localStorage
 */
export function createLocalStorageAdapter(config: ApiClientPlatformConfig): ApiClientConfig {
	return {
		...config,
		/**
		 * Retrieves the refresh token from localStorage.
		 * @returns The stored refresh token or null if not found
		 */
		getRefreshToken: async () => {
			return Promise.resolve(localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ?? undefined);
		},

		/**
		 * Stores or removes the refresh token in localStorage.
		 * If the token is undefined, removes the key entirely.
		 * @param refreshToken - The token to store or null to remove
		 */
		setRefreshToken: async (refreshToken) => {
			if (refreshToken === undefined) {
				return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN));
			} else {
				return Promise.resolve(localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken));
			}
		},

		/**
		 * Retrieves the account ID from localStorage.
		 * @returns The stored account ID or null if not found
		 */
		getAccountId: async () => {
			return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID) ?? undefined);
		},

		/**
		 * Stores or removes the account ID in localStorage.
		 * If the ID is falsy, removes the key entirely.
		 * @param accountId - The ID to store or null to remove
		 */
		setAccountId: async (accountId) => {
			if (accountId) {
				return Promise.resolve(localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId));
			} else {
				return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.ACCOUNT_ID));
			}
		},

		/**
		 * Retrieves the access token from localStorage.
		 * @returns The stored access token or null if not found
		 */
		getAccessToken: async () => {
			return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ?? undefined);
		},

		/**
		 * Stores or removes the access token in localStorage.
		 * If the token is undefined, removes the key entirely.
		 * @param accessToken - The token to store or null to remove
		 */
		setAccessToken: async (accessToken) => {
			if (accessToken) {
				return Promise.resolve(localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken));
			} else {
				return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN));
			}
		},

		/**
		 * Retrieves the local refresh timestamp from localStorage.
		 * Converts the stored string value to a number.
		 * @returns The timestamp as a number or null if not found
		 */
		getLocalRefreshedTime: async () => {
			const timestamp = localStorage.getItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME);
			return Promise.resolve(timestamp ? Number.parseInt(timestamp) : undefined);
		},

		/**
		 * Stores or removes the local refresh timestamp in localStorage.
		 * Converts the number to a string for storage.
		 * @param timestamp - The timestamp to store or null to remove
		 */
		setLocalRefreshedTime: async (timestamp) => {
			if (timestamp) {
				return Promise.resolve(localStorage.setItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME, timestamp.toString()));
			} else {
				return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME));
			}
		},

		/**
		 * Verifies if localStorage is available and functioning in the current environment.
		 *
		 * Performs the following checks:
		 * 1. Verifies that window.localStorage exists
		 * 2. Tests writing and reading from localStorage
		 * 3. Handles any permission or access errors
		 *
		 * @returns A boolean indicating if localStorage is supported
		 */
		isEnvironmentSupported: () => {
			try {
				// Check if localStorage is available
				if (typeof window === "undefined" || !window.localStorage) {
					return false;
				}

				// Test if we can write and read from localStorage
				const testKey = "__test_storage__";
				localStorage.setItem(testKey, "test");
				const testValue = localStorage.getItem(testKey);
				localStorage.removeItem(testKey);

				if (testValue !== "test") {
					return false;
				}

				return true;
			} catch {
				return false;
			}
		},
	};
}
